import { graphql, useFragment } from "react-relay";
import { BaseWidgetProps } from "../BaseProps";
import { NewsfeedWeb_widgetNewsfeed$key } from "./__generated__/NewsfeedWeb_widgetNewsfeed.graphql";
import React, {
  PropsWithChildren,
  ReactElement,
  useCallback,
  useState,
} from "react";
import { useInterval } from "../../useInterval";
import { WidgetNewsfeedPost } from "./WidgetNewsfeedPost";
import { AHText } from "arkh";
import { FormattedMessage } from "react-intl";

type Props = {
  widget: NewsfeedWeb_widgetNewsfeed$key;
  mode?: "FOCUS" | "IDLE";
  interactive?: undefined | false;
} & BaseWidgetProps;

export const NewsfeedWeb = (props: Props) => {
  const widget = useFragment<NewsfeedWeb_widgetNewsfeed$key>(
    graphql`
      fragment NewsfeedWeb_widgetNewsfeed on WidgetNewsfeed {
        posts {
          ...WidgetNewsfeedPost_widgetNewsfeedPost
        }
      }
    `,
    props.widget
  );

  const [currentPage, setCurrentPage] = useState(0);

  const onNextPost = useCallback(() => {
    setCurrentPage((currentPage + 1) % widget.posts.length);
  }, [currentPage, setCurrentPage, widget.posts.length]);

  if (widget.posts.length == 0) {
    return (
      <AHText type="info-small" style={{ textAlign: "center" }} space="large">
        <FormattedMessage
          description="WidgetNewsfeed empty"
          defaultMessage="There is nothing to show here right now."
        />
      </AHText>
    );
  }

  return (
    <>
      {widget.posts.map((post, i) => (
        <CrossFade key={i} active={i === currentPage}>
          <WidgetNewsfeedPost
            key={i}
            post={post}
            ImageWrapper={ImagesWrapper}
            active={i === currentPage}
            onNextPost={onNextPost}
          />
        </CrossFade>
      ))}
    </>
  );
};

const ImagesWrapper = (props: {
  children: ReactElement[];
  active: boolean;
  onNextPost?: () => void;
}) => {
  const [currentImage, setCurrentImage] = useState(0);

  useInterval(
    () => {
      if (currentImage + 1 >= props.children.length) {
        props.onNextPost?.();
      }
      setCurrentImage((currentImage + 1) % props.children.length);
    },
    props.active ? 12000 : null
  );

  return (
    <>
      {props.children.map((item, i) => (
        <CrossFade key={i} active={i === currentImage}>
          {item}
        </CrossFade>
      ))}
    </>
  );
};

const CrossFade = ({
  active,
  children,
}: PropsWithChildren<{ active?: boolean }>) => {
  return (
    <div
      style={{
        position: "absolute",
        width: "100%",
        height: "100%",
        top: 0,
        left: 0,
        opacity: active ? 1 : 0,
        transition: ".4s opacity",
        overflow: "hidden",
        display: "flex",
      }}
    >
      {children}
    </div>
  );
};
